import { useState } from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { Reveal, SectionHeading, SectionCta } from "./Reveal";

const ITEMS = [
  "You check the missed call log yourself, after hours, because nobody else will.",
  "You have answered patient DMs from your own bed.",
  "There are inquiries in your CRM nobody has touched in ten days.",
  "You trained a front desk hire, watched them leave, and started over.",
  "You cannot quote last month's conversion rate without pulling it yourself.",
  "Two free consults happened last week. Neither one got a follow-up.",
  "You raise ad spend when the schedule looks thin, even though that is not the real problem.",
  "You are the last line of defense in your own business, and it never stops.",
];

export const Checklist = () => {
  const [checked, setChecked] = useState(new Set());

  const toggle = (i) => {
    setChecked((prev) => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });
  };

  return (
    <section id="leak" data-testid="checklist-section" className="py-24 md:py-36">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <SectionHeading
            eyebrow="The part nobody warned you about"
            title={
              <>
                You trained for years to be a clinician,{" "}
                <span className="italic text-champagne">and somehow became the receptionist too.</span>
              </>
            }
            copy="The exhaustion was never about the medicine. It is everything wrapped around it. Be honest about how many of these are true this week."
          />
          <Reveal delay={0.15} className="mt-10">
            <div className="inline-flex items-baseline gap-3 border border-black/10 bg-coal px-6 py-4 shadow-sm">
              <motion.span
                key={checked.size}
                initial={{ scale: 1.5, opacity: 0.3 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 320, damping: 16 }}
                className="font-display text-5xl text-champagne inline-block"
                data-testid="checklist-counter"
              >
                {checked.size}
              </motion.span>
              <span className="text-sm text-zinc-500">of 8 true this week</span>
            </div>
            {checked.size >= 5 && (
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-6 text-sm text-champagne max-w-xs leading-relaxed"
                data-testid="checklist-verdict"
              >
                That is not a discipline problem. It is missing infrastructure. Until someone installs it, it lands on you.
              </motion.p>
            )}
          </Reveal>
        </div>

        <div className="lg:col-span-7 space-y-3">
          {ITEMS.map((item, i) => {
            const active = checked.has(i);
            return (
              <Reveal key={i} delay={i * 0.06}>
                <motion.button
                  onClick={() => toggle(i)}
                  data-testid={`checklist-item-${i}`}
                  aria-pressed={active}
                  whileHover={{ x: 6 }}
                  whileTap={{ scale: 0.99 }}
                  className={`w-full flex items-start gap-5 text-left border px-6 py-5 shadow-sm transition-colors duration-300 group ${
                    active
                      ? "border-champagne/50 bg-champagne/5"
                      : "border-black/10 bg-coal hover:border-champagne/40"
                  }`}
                >
                  <span
                    className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center border transition-colors duration-300 ${
                      active ? "border-champagne bg-champagne text-white" : "border-black/20 text-transparent group-hover:border-champagne/60"
                    }`}
                  >
                    <motion.span
                      key={String(active)}
                      initial={{ scale: 0, rotate: -45 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ type: "spring", stiffness: 500, damping: 20 }}
                    >
                      <Check size={14} strokeWidth={3} />
                    </motion.span>
                  </span>
                  <span
                    className={`text-base md:text-lg font-light leading-relaxed transition-colors duration-300 ${
                      active ? "text-zinc-500 line-through decoration-champagne/60" : "text-pearl"
                    }`}
                  >
                    {item}
                  </span>
                </motion.button>
              </Reveal>
            );
          })}
        </div>
      </div>
      <SectionCta id="checklist" label="Take this off my plate" />
    </section>
  );
};
